/**
 * Resource date-exceptions panel (DNKADM-0054 U2) — the list + add/remove for
 * `GET/POST/DELETE /v2/admin/resources/{id}/date-exceptions` (handoff §5.7).
 *
 * Mirrors the staff date-exceptions panel (`StaffDateExceptionsPanel`, DNKADM-0053).
 * Each exception is a date (`YYYY-MM-DD`, native `<TextField type="date">`), an
 * Available/Off toggle, and a **scope** (`ALL` = every location, or `LOCATION` = one
 * assigned location, with a location select). An "Off" date removes availability that
 * day; an "Available" date carries 1..n override shifts (same shift rules as the weekly
 * grid, the SHARED `@/utils/shiftValidation`). Each exception is added with its own POST
 * and removed with its own DELETE (the sub-resource is per-row, not a whole-list PUT),
 * with a refetch + toast after each write.
 *
 * The add form validates before POSTing: a date is required, a `LOCATION` scope needs a
 * location, and an available exception needs ≥1 valid non-overlapping shift. A
 * validation `400` / `409` renders inline on the add form; anything else raises an error
 * toast (`classifyFormError`).
 *
 * Native MUI date/time inputs only — NO `@mui/x-date-pickers`.
 */
import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, inkAlpha, resolveTokens } from '@/theme';
import {
  createResourceDateException,
  deleteResourceDateException,
  type ExceptionScope,
  type ResourceDateException,
  type ResourceDateExceptionInput,
  type ResourceShift,
} from '@/api/resources';
import type { V2Location } from '@/api/locations';
import { serverMessage } from '@/api/serverMessage';
import { validateShifts } from '@/utils/shiftValidation';
import { dateLabel } from '@/utils/clockTime';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';

interface ResourceDateExceptionsPanelProps {
  /** The loaded date exceptions for the resource. */
  exceptions: ResourceDateException[];
  /** The resource id, forwarded on writes. */
  resourceId: string;
  /** The resource's assigned locations (the `LOCATION`-scope options). */
  locations: V2Location[];
  /** Re-fetch after a successful write (refetch-after-write). */
  refetch: () => void;
  /** Raise a non-blocking toast (success / upstream-error). */
  showToast: ShowToast;
}

/** A blank shift for a freshly-available exception / an added shift row. */
function blankShift(): ResourceShift {
  return { startTime: '', endTime: '' };
}

/** Human label for a location id (name, falling back to id), or a generic note. */
function locationLabel(locations: V2Location[], id: string | null): string {
  if (id === null) return 'All locations';
  const loc = locations.find((l) => l.id === id);
  return loc ? loc.name || loc.id : id;
}

export function ResourceDateExceptionsPanel({
  exceptions,
  resourceId,
  locations,
  refetch,
  showToast,
}: ResourceDateExceptionsPanelProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  // Add-form state.
  const [date, setDate] = useState('');
  const [isAvailable, setIsAvailable] = useState(false);
  const [scope, setScope] = useState<ExceptionScope>('ALL');
  const [locationId, setLocationId] = useState('');
  const [shifts, setShifts] = useState<ResourceShift[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  function resetForm() {
    setDate('');
    setIsAvailable(false);
    setScope('ALL');
    setLocationId('');
    setShifts([]);
    setFormError(null);
  }

  function handleToggleAvailable(available: boolean) {
    setIsAvailable(available);
    setShifts(available ? [blankShift()] : []);
  }

  function updateShift(index: number, patch: Partial<ResourceShift>) {
    setShifts((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  async function handleAdd() {
    setFormError(null);
    if (date.trim() === '') {
      setFormError('Choose a date for the exception.');
      return;
    }
    if (scope === 'LOCATION' && locationId === '') {
      setFormError('Choose a location for a location-scoped exception.');
      return;
    }
    if (isAvailable) {
      const message = validateShifts(shifts);
      if (message !== null) {
        setFormError(message);
        return;
      }
    }

    const input: ResourceDateExceptionInput = {
      exceptionDate: date.trim(),
      isAvailable,
      scope,
      shifts: isAvailable ? shifts : [],
    };
    if (scope === 'LOCATION') input.locationId = locationId;

    setSaving(true);
    try {
      await createResourceDateException(resourceId, input);
      resetForm();
      refetch();
      showToast('Date exception added', 'success');
    } catch (err) {
      const view = classifyFormError(err, 'The date exception couldn’t be added. Please try again.');
      if (view.kind === 'inline') setFormError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(exception: ResourceDateException) {
    if (exception.id === null) return;
    setRemovingId(exception.id);
    try {
      await deleteResourceDateException(resourceId, exception.id);
      refetch();
      showToast('Date exception removed', 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? 'Couldn’t remove the date exception.', 'error');
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      {/* Existing exceptions */}
      <Paper elevation={1} sx={{ px: 2.75, py: exceptions.length === 0 ? 2.5 : 0.75, mb: 2 }}>
        {exceptions.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No date exceptions. Add one to mark this resource off on a specific date or set
            special hours.
          </Typography>
        ) : (
          exceptions.map((exception, index) => (
            <Stack
              key={exception.id ?? `${exception.exceptionDate}-${index}`}
              direction="row"
              sx={{
                alignItems: 'center',
                gap: 1.5,
                py: 1.5,
                borderBottom: index === exceptions.length - 1 ? 'none' : '1px solid',
                borderColor: t.lineFaint,
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontSize: 13.5, fontWeight: 500, minWidth: 110 }}>
                {dateLabel(exception.exceptionDate) ?? exception.exceptionDate}
              </Typography>
              <Chip
                size="small"
                label={exception.isAvailable ? 'Available' : 'Off'}
                color={exception.isAvailable ? 'success' : 'default'}
                variant={exception.isAvailable ? 'filled' : 'outlined'}
              />
              <Chip
                size="small"
                variant="outlined"
                label={
                  exception.scope === 'LOCATION'
                    ? locationLabel(locations, exception.locationId)
                    : 'All locations'
                }
              />
              {exception.isAvailable && exception.shifts.length > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {exception.shifts.map((s) => `${s.startTime}–${s.endTime}`).join(', ')}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              <IconAction
                size="small"
                label={`Remove exception ${dateLabel(exception.exceptionDate) ?? exception.exceptionDate}`}
                onClick={() => void handleRemove(exception)}
                disabled={exception.id === null || removingId === exception.id}
              >
                <Icon name="delete" sx={{ fontSize: 20 }} />
              </IconAction>
            </Stack>
          ))
        )}
      </Paper>

      {/* Add form */}
      <Paper elevation={0} sx={{ p: 2.25, border: '1px solid', borderColor: inkAlpha(theme, 0.1), borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Add a date exception
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
            <TextField
              type="date"
              label="Date"
              size="small"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={saving}
              sx={{ width: 180 }}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { 'aria-label': 'Exception date' },
              }}
            />
            <TextField
              select
              label="Scope"
              size="small"
              value={scope}
              onChange={(e) => setScope(e.target.value as ExceptionScope)}
              disabled={saving}
              sx={{ width: 180 }}
              slotProps={{ htmlInput: { 'aria-label': 'Exception scope' } }}
            >
              <MenuItem value="ALL">All locations</MenuItem>
              <MenuItem value="LOCATION">One location</MenuItem>
            </TextField>
            {scope === 'LOCATION' && (
              <TextField
                select
                label="Location"
                size="small"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                disabled={saving || locations.length === 0}
                sx={{ width: 220 }}
                slotProps={{ htmlInput: { 'aria-label': 'Exception location' } }}
              >
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name === '' ? loc.id : loc.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={isAvailable}
                onChange={(e) => handleToggleAvailable(e.target.checked)}
                disabled={saving}
                slotProps={{ input: { 'aria-label': 'Exception available' } }}
              />
            }
            label="Available"
          />

          {isAvailable && (
            <Box>
              {shifts.map((shift, index) => (
                <Stack
                  key={index}
                  direction="row"
                  sx={{ alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}
                >
                  <TextField
                    type="time"
                    label="Start"
                    size="small"
                    value={shift.startTime}
                    onChange={(e) => updateShift(index, { startTime: e.target.value })}
                    disabled={saving}
                    sx={{ width: 140 }}
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: {
                        'aria-label': `Exception shift ${index + 1} start time (HH:mm, local)`,
                      },
                    }}
                  />
                  <TextField
                    type="time"
                    label="End"
                    size="small"
                    value={shift.endTime}
                    onChange={(e) => updateShift(index, { endTime: e.target.value })}
                    disabled={saving}
                    sx={{ width: 140 }}
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: {
                        'aria-label': `Exception shift ${index + 1} end time (HH:mm, local)`,
                      },
                    }}
                  />
                  <IconAction
                    size="small"
                    label={`Remove exception shift ${index + 1}`}
                    onClick={() => setShifts((prev) => prev.filter((_s, i) => i !== index))}
                    disabled={saving || shifts.length === 1}
                  >
                    <Icon name="close" sx={{ fontSize: 18 }} />
                  </IconAction>
                </Stack>
              ))}
              <Button
                size="small"
                startIcon={<Icon name="add" sx={{ fontSize: 18 }} />}
                onClick={() => setShifts((prev) => [...prev, blankShift()])}
                disabled={saving}
                sx={{ textTransform: 'none' }}
              >
                Add shift
              </Button>
            </Box>
          )}

          {formError && (
            <Typography role="alert" sx={{ fontSize: 13, color: 'error.main', fontWeight: 500 }}>
              {formError}
            </Typography>
          )}

          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={() => void handleAdd()} disabled={saving}>
              {saving ? 'Adding…' : 'Add exception'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
